import React from "react";
import type { FC, ReactNode } from "react";

import { type IntlFormatters, useIntl } from "react-intl";

import { useRecoilCallback } from "recoil";

import {
  BADGE_KIND_SECONDARY,
  BADGE_TYPE_DEFAULT,
  BADGE_TYPE_ERROR,
  BADGE_TYPE_SUCCESS,
  BADGE_TYPE_WARNING,
  Badge,
  ContextMenu,
  Flex,
  Link,
  TEXT_VARIANT_LIGHT,
  Text,
  makeButtonWithMenu,
} from "mailup-web-components";

import type { ChannelSubscriptionStatus } from "Pages/RecipientsList/model";

import { panelEditRecipientChangeStatusCallback } from "Pages/RecipientsList/recoil/callbacks/recipient";

import IconChevronDownRegular from "Components/icons/IconChevronDownRegular";

const getBadgeStatus = (status: ChannelSubscriptionStatus): string => {
  switch (status) {
    case "SUBSCRIBED":
      return BADGE_TYPE_SUCCESS;
    case "PENDING":
      return BADGE_TYPE_WARNING;
    case "UNSUBSCRIBED":
      return BADGE_TYPE_ERROR;
    default:
      return BADGE_TYPE_DEFAULT;
  }
};

const getBadgeText = (
  intl: IntlFormatters,
  status: ChannelSubscriptionStatus,
): string => {
  switch (status) {
    case "SUBSCRIBED":
      return intl.formatMessage({
        id: "Load.Common.StatusRecipientSubscribedSingular",
      });
    case "PENDING":
      return intl.formatMessage({
        id: "Load.Common.StatusRecipientPendingSingular",
      });
    case "UNSUBSCRIBED":
      return intl.formatMessage({
        id: "Load.Common.StatusRecipientUnsibscribedSingular",
      });
    default:
      return intl.formatMessage({
        id: "Load.Common.StatusRecipientNotSubscribedSingular",
      });
  }
};

interface ButtonComponentProps {
  status: ChannelSubscriptionStatus;
  icon?: ReactNode;
}

const ButtonComponent: FC<ButtonComponentProps> = ({ status, icon }) => {
  const intl = useIntl();

  return (
    <Link alignContent data-mu-test="recipient-status-change" gap="8px">
      <Badge
        className="e-badge--clickable"
        kind={BADGE_KIND_SECONDARY}
        type={getBadgeStatus(status)}
        text={
          (
            <Flex alignItems="center" gap="4px" display="inline-flex">
              {getBadgeText(intl, status)}
              <span style={{ position: "relative", top: "-1px" }}>{icon}</span>
            </Flex>
          ) as unknown as string
        }
      />

      <Text variant={TEXT_VARIANT_LIGHT} alignContent>
        <IconChevronDownRegular width="12px" />
      </Text>
    </Link>
  );
};

interface MenuComponentProps {
  channel: "EMAIL" | "SMS";
  status: ChannelSubscriptionStatus;
}

const MenuComponent: FC<MenuComponentProps> = ({ status, channel }) => {
  const intl = useIntl();

  const changeStatus = useRecoilCallback(
    panelEditRecipientChangeStatusCallback,
    [],
  );

  const items = [
    {
      label: intl.formatMessage({
        id: "Load.RecipientsList.ActionSubscribe",
      }),
      "data-mu-test": "recipient-action-subscribe",
      value: "SUBSCRIBED",
      onClick: () => {
        void changeStatus(channel, "SUBSCRIBED");
      },
    },
    {
      label: intl.formatMessage({
        id: "Load.RecipientsList.ActionPending",
      }),
      "data-mu-test": "recipient-action-pending",
      value: "PENDING",
      onClick: () => {
        void changeStatus(channel, "PENDING");
      },
    },
    {
      label: intl.formatMessage({
        id: "Load.RecipientsList.ActionUnsubscribe",
      }),
      "data-mu-test": "recipient-action-unsubscribe",
      value: "UNSUBSCRIBED",
      onClick: () => {
        void changeStatus(channel, "UNSUBSCRIBED");
      },
    },
  ];

  return <ContextMenu items={items} selected={status} />;
};

interface EditStatusProps {
  status: ChannelSubscriptionStatus;
  channel: "EMAIL" | "SMS";
  icon?: ReactNode;
}

const withButtonProps =
  // eslint-disable-next-line react/display-name
  (status: ChannelSubscriptionStatus, icon?: ReactNode) => () => (
    <ButtonComponent status={status} icon={icon} />
  );

const withMenuProps =
  (channel: "EMAIL" | "SMS", status: ChannelSubscriptionStatus) =>
  // eslint-disable-next-line react/display-name
  () => <MenuComponent channel={channel} status={status} />;

const EditStatus: FC<EditStatusProps> = ({ status, channel, icon }) => {
  const Ctrl = makeButtonWithMenu(
    withButtonProps(status, icon),
    withMenuProps(channel, status),
  );

  return (
    <Flex display="inline-flex">
      <Ctrl
        data-mu-test={`recipient-edit-status--${channel}`}
        direction="down"
      />
    </Flex>
  );
};

export default EditStatus;
